import React from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { setFormData } from '@plone/volto/actions/form/form';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import undoSVG from '@plone/volto/icons/undo.svg';
import checkSVG from '@plone/volto/icons/check.svg';
import forbiddenSVG from '@plone/volto/icons/forbidden.svg';
import errorSVG from '@plone/volto/icons/error.svg';
import unlockSVG from '@plone/volto/icons/unlock.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import flashSVG from '@plone/volto/icons/flash.svg';
import downKeySVG from '@plone/volto/icons/down-key.svg';
import upKeySVG from '@plone/volto/icons/up-key.svg';
import AssistantMarkdown from './AssistantMarkdown';
import { diffWords, diffExcerpt } from './diff';
import { moveCursor } from './cursor';

/**
 * What a failed turn says to the person who asked. Never a status code, never
 * the service's own wording: an editor can act on "try again", not on "500".
 * The technical detail is in the browser console.
 */
const errorMessages = defineMessages({
  unreachable: {
    id: 'assistantErrorUnreachable',
    defaultMessage:
      'The assistant is not available at the moment. Please try again in a few minutes.',
  },
  unauthorised: {
    id: 'assistantErrorUnauthorised',
    defaultMessage:
      'The assistant refused this request. Your session may have expired — reload the page and log in again.',
  },
  busy: {
    id: 'assistantErrorBusy',
    defaultMessage:
      'The assistant is busy right now. Please try again in a moment.',
  },
  failed: {
    id: 'assistantErrorFailed',
    defaultMessage:
      'The assistant could not finish this task. Try rephrasing your request, or send it again.',
  },
  cancelled: {
    id: 'assistantErrorCancelled',
    defaultMessage: 'This request was stopped before it finished.',
  },
  noConversation: {
    id: 'assistantErrorNoConversation',
    defaultMessage:
      'This conversation is no longer open. Start a new one and send your message again.',
  },
});

const messages = defineMessages({
  undoChange: {
    id: 'Undo this change',
    defaultMessage: 'Undo this change',
  },
  retryWrite: {
    id: 'Send again with Write',
    defaultMessage: 'Send again with Write',
  },
  cancelled: {
    id: 'Cancelled',
    defaultMessage: 'Cancelled',
  },
  earlierSteps: {
    id: 'assistantEarlierSteps',
    defaultMessage:
      '{count, plural, one {# earlier step} other {# earlier steps}}',
  },
  stepsSummary: {
    id: 'assistantStepsSummary',
    defaultMessage: '{count, plural, one {# step} other {# steps}}',
  },
  showSteps: {
    id: 'assistantShowSteps',
    defaultMessage: 'Show all steps',
  },
  hideSteps: {
    id: 'assistantHideSteps',
    defaultMessage: 'Hide steps',
  },
  locateBlock: {
    id: 'assistantShowBlockHint',
    defaultMessage: 'Show this block on the page',
  },
  showChange: {
    id: 'assistantShowChange',
    defaultMessage: 'Show the full change',
  },
  hideChange: {
    id: 'assistantHideChange',
    defaultMessage: 'Hide the full change',
  },
  imageBefore: {
    id: 'assistantImageBefore',
    defaultMessage: 'Before',
  },
  imageAfter: {
    id: 'assistantImageAfter',
    defaultMessage: 'After',
  },
  imageGenerated: {
    id: 'assistantImageGenerated',
    defaultMessage: 'Generated',
  },
  working: {
    id: 'assistantWorking',
    defaultMessage: 'Working',
  },
});

// Up to this many steps an activity run just shows them all; beyond it,
// the run the agent is still working on windows to the newest steps and
// finished runs collapse into a one-line summary.
const WINDOW = 4;

// Diff renderings per tool call, computed once — the rail re-renders on
// every stream chunk and the word diff is not free on long paragraphs.
const diffCache = new WeakMap();

const cacheFor = (toolCall) => {
  let cached = diffCache.get(toolCall);
  if (!cached) {
    cached = {};
    diffCache.set(toolCall, cached);
  }
  return cached;
};

const excerptFor = (toolCall) => {
  if (toolCall.before == null || toolCall.after == null) return null;
  const cached = cacheFor(toolCall);
  if (!('excerpt' in cached)) {
    cached.excerpt = diffExcerpt(toolCall.before, toolCall.after);
  }
  return cached.excerpt;
};

const fullDiffFor = (toolCall) => {
  const cached = cacheFor(toolCall);
  if (!('full' in cached)) {
    cached.full = diffWords(toolCall.before, toolCall.after);
  }
  return cached.full;
};

// Displayable src for an image a step touched: block urls pointing at site
// content get their image scale endpoint appended; data URLs and external
// images render as they are.
const imageSrc = (url) => {
  if (url.startsWith('data:')) return url;
  const flattened = flattenToAppURL(url);
  return /^https?:\/\//.test(flattened)
    ? flattened
    : `${flattened}/@@images/image`;
};

const renderDiffParts = (parts) =>
  parts.map((part, index) =>
    part.type === 'del' ? (
      <del key={index}>{part.text}</del>
    ) : part.type === 'ins' ? (
      <ins key={index}>{part.text}</ins>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    ),
  );

/**
 * One conversation entry.
 *
 * User turns render as a bubble. Assistant turns render their parts in
 * emission order: text segments (narration between tool calls and the
 * final reply are indistinguishable — the agent may always reach for
 * another tool after talking) interleaved with activity runs, one row per
 * tool call. Step descriptions locate their block on the page; steps that
 * rewrote text carry a compact diff that expands to the full change on
 * click. The newest turn that changed the current page offers "undo this
 * change" (the panel decides via `canUndo` — also for cancelled turns
 * whose steps already landed); permission-denied turns offer a one-click
 * retry with write permission.
 */
const AssistantMessage = ({ message, canRestore, canUndo, onRetryWrite }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [expandedRuns, setExpandedRuns] = React.useState({});
  const [expandedDiffs, setExpandedDiffs] = React.useState({});

  if (message.role === 'user') {
    return (
      <div className="assistant-message assistant-message--user">
        {message.attachments?.length > 0 && (
          <div className="assistant-message-attachments">
            {message.attachments.map((attachment, index) => (
              <Image
                key={index}
                src={attachment.dataUrl}
                alt={attachment.name}
              />
            ))}
          </div>
        )}
        {(message.references?.length > 0 || message.skills?.length > 0) && (
          <div className="assistant-message-references">
            {(message.skills || []).map((skill) => (
              <span key={`skill:${skill.name}`} title={skill.description}>
                <Icon name={flashSVG} size="11px" />
                {skill.title}
              </span>
            ))}
            {(message.references || []).map((reference) => (
              <span key={reference['@id']} title={reference['@id']}>
                <Icon name={linkSVG} size="11px" />
                {reference.title}
              </span>
            ))}
          </div>
        )}
        {message.content}
      </div>
    );
  }

  const running = message.status === 'running';
  const parts = message.parts || [];
  // Group consecutive tool calls into runs; texts stand on their own.
  const units = [];
  parts.forEach((part, partIndex) => {
    if (part.kind === 'tool') {
      const last = units[units.length - 1];
      const step = { toolCall: part.toolCall, key: partIndex };
      if (last?.kind === 'run') last.steps.push(step);
      else units.push({ kind: 'run', steps: [step] });
    } else {
      units.push({ kind: 'text', text: part.text });
    }
  });
  // The spinner sits on the newest step only while the agent has not
  // started talking again after it.
  const toolIsLive = running && parts[parts.length - 1]?.kind === 'tool';

  const renderStep = ({ toolCall, key }, current) => {
    const excerpt = excerptFor(toolCall);
    const diffExpanded = !!expandedDiffs[key];
    const locatable = canRestore && toolCall.blockId;
    return (
      <div
        key={key}
        className={`assistant-activity-step${
          toolCall.blocked || toolCall.failed ? ' blocked' : ''
        }${excerpt || toolCall.image ? ' with-diff' : ''}`}
      >
        {toolCall.blocked ? (
          <Icon name={forbiddenSVG} size="14px" />
        ) : toolCall.failed ? (
          // A step that came to nothing. Drawing it with the same tick as
          // its neighbours would claim work that never happened.
          <Icon name={errorSVG} size="14px" />
        ) : current ? (
          <span className="assistant-step-spinner" />
        ) : (
          <Icon name={checkSVG} size="14px" />
        )}
        <span className="assistant-activity-text">
          {locatable ? (
            <button
              type="button"
              className="assistant-activity-locate"
              title={intl.formatMessage(messages.locateBlock)}
              onClick={() =>
                moveCursor(toolCall.blockId, { follow: true, transient: true })
              }
            >
              {toolCall.description}
            </button>
          ) : (
            toolCall.description
          )}
          {toolCall.image && (
            <span className="assistant-activity-imagediff">
              {toolCall.image.before && (
                <>
                  <Image
                    className="before"
                    src={imageSrc(toolCall.image.before)}
                    alt={intl.formatMessage(messages.imageBefore)}
                  />
                  <Icon name={aheadSVG} size="12px" />
                </>
              )}
              <Image
                className="after"
                src={imageSrc(toolCall.image.after)}
                alt={intl.formatMessage(
                  toolCall.image.before
                    ? messages.imageAfter
                    : messages.imageGenerated,
                )}
              />
            </span>
          )}
          {excerpt && (
            <button
              type="button"
              className={`assistant-activity-diff${
                diffExpanded ? ' expanded' : ''
              }`}
              aria-expanded={diffExpanded}
              title={intl.formatMessage(
                diffExpanded ? messages.hideChange : messages.showChange,
              )}
              onClick={() =>
                setExpandedDiffs((prev) => ({ ...prev, [key]: !prev[key] }))
              }
            >
              <span>
                {renderDiffParts(
                  diffExpanded ? fullDiffFor(toolCall) : excerpt,
                )}
              </span>
            </button>
          )}
        </span>
      </div>
    );
  };

  const renderRun = (unit, unitIndex) => {
    const { steps } = unit;
    const total = steps.length;
    const collapsible = total > WINDOW;
    // Only the run the agent is currently in keeps growing.
    const active = running && unitIndex === units.length - 1;
    let header = null;
    let rows;
    if (active) {
      const hiddenCount = collapsible ? total - WINDOW : 0;
      if (hiddenCount > 0) {
        header = (
          <div className="assistant-activity-earlier">
            <Icon name={checkSVG} size="14px" />
            <span>
              {intl.formatMessage(messages.earlierSteps, {
                count: hiddenCount,
              })}
            </span>
          </div>
        );
      }
      rows = steps
        .slice(hiddenCount)
        .map((step, index) =>
          renderStep(step, toolIsLive && hiddenCount + index === total - 1),
        );
    } else if (collapsible) {
      const runExpanded = !!expandedRuns[unitIndex];
      header = (
        <button
          type="button"
          className="assistant-activity-toggle"
          aria-expanded={runExpanded}
          title={intl.formatMessage(
            runExpanded ? messages.hideSteps : messages.showSteps,
          )}
          onClick={() =>
            setExpandedRuns((prev) => ({
              ...prev,
              [unitIndex]: !prev[unitIndex],
            }))
          }
        >
          <Icon name={checkSVG} size="14px" />
          <span>
            {intl.formatMessage(messages.stepsSummary, { count: total })}
          </span>
          <Icon name={runExpanded ? upKeySVG : downKeySVG} size="14px" />
        </button>
      );
      rows = runExpanded
        ? steps.map((step) => renderStep(step, false))
        : // Blocked steps carry the permission story — never hide them.
          steps
            .filter((step) => step.toolCall.blocked)
            .map((step) => renderStep(step, false));
    } else {
      rows = steps.map((step) => renderStep(step, false));
    }
    return (
      <div key={`run-${unitIndex}`} className="assistant-activity">
        {header}
        {rows}
      </div>
    );
  };

  return (
    <div className="assistant-message assistant-message--assistant">
      {units.map((unit, unitIndex) =>
        unit.kind === 'run' ? (
          renderRun(unit, unitIndex)
        ) : (
          <AssistantMarkdown
            key={`text-${unitIndex}`}
            streaming={running && unitIndex === units.length - 1}
          >
            {unit.text}
          </AssistantMarkdown>
        ),
      )}
      {running && parts.length === 0 && (
        <div
          className="assistant-message-spinner"
          role="status"
          aria-label={intl.formatMessage(messages.working)}
        >
          <span />
          <span />
          <span />
        </div>
      )}
      {message.status === 'cancelled' && (
        <div className="assistant-message-cancelled">
          {intl.formatMessage(messages.cancelled)}
        </div>
      )}
      {message.status === 'done' && message.meta?.error && (
        <div className="assistant-message-error">
          {intl.formatMessage(
            errorMessages[message.meta.error.kind] || errorMessages.failed,
          )}
        </div>
      )}
      {((message.status === 'done' && message.meta?.denied) ||
        (canUndo && message.before)) && (
        <div className="assistant-message-actions">
          {message.status === 'done' && message.meta?.denied && (
            <button
              type="button"
              className="assistant-message-action primary"
              onClick={() => onRetryWrite(message)}
            >
              <Icon name={unlockSVG} size="14px" />
              {intl.formatMessage(messages.retryWrite)}
            </button>
          )}
          {canUndo && message.before && (
            <button
              type="button"
              className="assistant-message-action"
              onClick={() => dispatch(setFormData(message.before))}
            >
              <Icon name={undoSVG} size="14px" />
              {intl.formatMessage(messages.undoChange)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantMessage;
